import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot } from '@angular/router';
import { environment } from 'src/environments/environment';

interface SeoData {
  title: string;
  description: string;
  type?: string;
  image?: string;
  images?: string[];
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));
  private readonly siteName = 'Qatch';
  private readonly defaultImage = 'https://firebasestorage.googleapis.com/v0/b/qatchcode-production.appspot.com/o/qatch-web%2Fbanner1.jpeg?alt=media';
  private readonly defaultDescription = 'Discover personalized fashion, trending styles, and products curated for you by Qatch.';
  private readonly origin = environment.appEnv === 'uat' ? 'https://uat.joinqatch.com' : 'https://joinqatch.com';

  constructor(
    private titleService: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}

  update(route: ActivatedRouteSnapshot, url: string): void {
    const routeData = this.getDeepestRouteData(route);
    const path = this.normalizePath(url);
    const seoData = this.getSeoData(path, routeData);
    this.updateHead(path, seoData, seoData.type || 'WebSite');
  }

  updatePage(url: string, title: string, description: string, image?: string, images: string[] = []): void {
    const path = this.normalizePath(url);
    const pageImage = image || this.defaultImage;
    this.updateHead(path, {
      title,
      description,
      type: 'website',
      image: pageImage,
      images: [pageImage, ...images.filter(value => value !== pageImage)]
    }, 'WebSite');
  }

  updateCollection(url: string, collection: any, products: any[] = []): void {
    const path = this.normalizePath(url);
    const title = collection?.preview_title || collection?.title || collection?.shop_title || collection?.name || 'Curated Collection';
    const description = collection?.seo_description
      || `Shop ${title} curated by Qatch. Discover from premium brands. New styles added daily.`;
      const image = this.firstImage(collection?.preview_logo, collection?.hero_image, collection?.banner_image,
        collection?.image_url, collection?.logo);
    const images = [
      image,
      ...products.map(product => product?.image_url),
      ...(collection?.products || []).map(product => product?.image_url)
    ].filter((value, index, all) => this.isImageUrl(value) && all.indexOf(value) === index).slice(0, 3);
    const canonicalUrl = `${this.origin}${path}`;

    this.updateHead(path, { title, description, type: 'website', image, images }, {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: title,
      description,
      url: canonicalUrl,
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: products.length,
      image: images,
      itemListElement: products.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.product_name || product.name || `Product ${product.product_id || index + 1}`,
          image: product.image_url || undefined,
          description: product.description || undefined,
          brand: product.brand_name ? { '@type': 'Brand', name: product.brand_name } : undefined,
          url: this.cleanProductUrl(product),
          offers: this.buildProductOffer(product)
        }
      }))
    });
  }

  updateTopStyles(url: string, products: any[] = []): void {
    const path = this.normalizePath(url);
    const title = 'Collections';
    const description = 'New styles dropped daily. Save products you love for later with a heart and click for product details!';
    const images = products.map(product => product?.image_url || product?.imageUrl)
      .filter((value, index, all) => value && all.indexOf(value) === index)
      .slice(0, 3);

    this.updateHead(path, { title, description, type: 'website', image: images[0], images }, {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: title,
      description,
      url: `${this.origin}${path}`,
      numberOfItems: products.length,
      image: images,
      itemListElement: products.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.product_name || product.name || `Product ${product.product_id || index + 1}`,
          image: product.image_url || product.imageUrl || undefined,
          url: this.cleanProductUrl(product),
          offers: this.buildProductOffer(product)
        }
      }))
    });
  }

  updateOutfit(url: string, outfit: any, products: any[] = []): void {
    const path = this.normalizePath(url);
    const authorName = outfit?.author?.name || 'Qatch member';
    const title = outfit?.title && !this.isUuid(outfit.title)
      ? outfit.title
      : `${authorName}'s Outfit`;
    const description = outfit?.description || `${authorName}'s Outfit`;
    const image = outfit?.imageUrl || outfit?.image_url;
    const images = [image, ...products.map(product => product?.imageUrl || product?.image_url)]
      .filter((value, index, all) => value && all.indexOf(value) === index)
      .slice(0, 3);

    this.updateHead(path, { title, description, type: 'article', image, images }, {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description,
      url: `${this.origin}${path}`,
      image: images,
      author: { '@type': 'Person', name: authorName },
      publisher: { '@type': 'Organization', name: this.siteName, url: this.origin }
    });
  }

  updateWishlist(url: string, user: any, products: any[] = []): void {
    const path = this.normalizePath(url);
    const firstName = user?.first_name || 'Qatch member';
    const title = `${firstName}'s Wishlist | New Products Added`;
    const description = "Products added to the user's wishlist by Qatch's stylists.";
    const images = products.map(product => product?.image_url || product?.imageUrl)
      .filter((value, index, all) => value && all.indexOf(value) === index)
      .slice(0, 3);

    this.updateHead(path, { title, description, type: 'website', image: images[0], images }, {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: title,
      description,
      url: `${this.origin}${path}`,
      numberOfItems: products.length,
      image: images,
      itemListElement: products.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.product_name || product.name || `Product ${product.product_id || index + 1}`,
          image: product.image_url || product.imageUrl || undefined,
          url: this.cleanProductUrl(product),
          offers: this.buildProductOffer(product)
        }
      }))
    });
  }

  private updateHead(path: string, seoData: SeoData, structuredData: object | string): void {
    const fullTitle = seoData.title === this.siteName ? seoData.title : `${seoData.title} | ${this.siteName}`;
    const canonicalUrl = `${this.origin}${path}`;

    this.titleService.setTitle(fullTitle);
    this.meta.updateTag({ name: 'description', content: seoData.description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ property: 'og:title', content: fullTitle });
    this.meta.updateTag({ property: 'og:description', content: seoData.description });
    this.meta.updateTag({ property: 'og:type', content: seoData.type || 'website' });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:site_name', content: this.siteName });
    this.meta.updateTag({ property: 'og:image:alt', content: fullTitle });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: fullTitle });
    this.meta.updateTag({ name: 'twitter:description', content: seoData.description });
    this.meta.updateTag({ name: 'twitter:image:alt', content: fullTitle });
    if (seoData.images?.length) {
      this.meta.updateTag({ name: 'thumbnail', content: seoData.images[0] });
    }
    if (this.isImageUrl(seoData.image)) {
      this.meta.updateTag({ property: 'og:image', content: seoData.image });
      this.meta.updateTag({ name: 'twitter:image', content: seoData.image });
    }
    this.setCanonical(canonicalUrl);
    this.setStructuredData(structuredData === 'WebSite'
      ? {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: fullTitle,
          description: seoData.description,
          url: canonicalUrl,
          publisher: { '@type': 'Organization', name: this.siteName, url: this.origin }
        }
      : structuredData as object);
  }

  private getDeepestRouteData(route: ActivatedRouteSnapshot): Record<string, string> {
    let current = route;
    while (current.firstChild) current = current.firstChild;
    return current.data || {};
  }

  private isUuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value || '');
  }

  private getSeoData(path: string, routeData: Record<string, string>): SeoData {
    if (path === '/' || path === '/home') {
      return { title: this.siteName, description: 'Discover personalized fashion, trending styles, and products curated for you by Qatch.', image: this.defaultImage, images: [this.defaultImage] };
    }
    if (path === '/discover/collections') {
      return { title: 'Top Styles', description: 'Explore trending styles, curated collections, and products selected by Qatch stylists.', type: 'website', image: this.defaultImage, images: [this.defaultImage] };
    }
    if (path.startsWith('/collections/')) {
      return { title: routeData['title'] || 'Curated Collection', description: 'Shop curated fashion collections and discover products selected by Qatch.', type: 'website' };
    }
    if (path.startsWith('/users/') && path.includes('/outfits')) {
      return { title: routeData['title'] || 'Style Outfit', description: 'Explore a personalized outfit and discover products selected by Qatch.', type: 'article', image: this.defaultImage, images: [this.defaultImage] };
    }
    if (path === '/internal/requests/style-me') {
      return { title: 'Get Styled by Qatch: Complete Your Request', description: this.defaultDescription };
    }
    return {
      title: routeData['title'] || this.siteName,
      description: routeData['description'] || this.defaultDescription,
      image: this.defaultImage,
      images: [this.defaultImage],
    };
  }

  private normalizePath(url: string): string {
    const path = url.split('?')[0].split('#')[0] || '/';
    if (path.length > 1) return path.replace(/\/$/, '');
    return path;
  }

  private firstImage(...values: unknown[]): string | undefined {
    return values.find((value): value is string => this.isImageUrl(value));
  }

  private isImageUrl(value: unknown): value is string {
    return typeof value === 'string' && /^https?:\/\/\S+$/i.test(value) && !/\s/.test(value);
  }

  private cleanProductUrl(product: any): string | undefined {
    if (this.isServer && product?.product_id) {
      return `http://redirect.joinqatch.com/products/${encodeURIComponent(product.product_id)}`;
    }
    const url = product?.product_url || product?.productUrl || product?.url;
    return typeof url === 'string' && /^https?:\/\/\S+$/i.test(url) ? url : undefined;
  }

  private buildProductOffer(product: any): Record<string, unknown> | undefined {
    const rawPrice = product?.discount_price ?? product?.price ?? product?.amount;
    if (rawPrice === undefined || rawPrice === null || rawPrice === '') return undefined;

    const numericPrice = Number(rawPrice);
    if (!Number.isFinite(numericPrice)) return undefined;

    return {
      '@type': 'Offer',
      price: numericPrice,
      priceCurrency: 'USD',
      availability: product?.availability_id === 2
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock',
      url: this.cleanProductUrl(product)
    };
  }

  private setCanonical(url: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private setStructuredData(data: object): void {
    let script = this.document.head.querySelector<HTMLScriptElement>('#qatch-seo-jsonld');
    if (!script) {
      script = this.document.createElement('script');
      script.id = 'qatch-seo-jsonld';
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }
}
